import { Capacitor } from "@capacitor/core";
import {
  AUTH_REQUIRED_EVENT,
  beginLogin,
  getAccessToken,
  refreshAfterRejection,
} from "./auth";

/**
 * Utility functions for API calls
 */

/**
 * Get the base URL for API calls
 * In development, this will be a relative URL that gets proxied to the backend
 * In production, this will be the absolute URL of the backend
 */
export const getApiBaseUrl = (): string => {
  // In Capacitor, we always need an absolute URL
  if (Capacitor.isNativePlatform()) {
    const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL;
    console.log("Capacitor Native Platform detected");
    if (configuredBaseUrl) {
      console.log(`Using configured VITE_API_BASE_URL: ${configuredBaseUrl}`);
      return configuredBaseUrl;
    }

    // Default URL for native app
    // Check if we are in staging mode (VITE_STAGING is often set during build)
    const isStaging =
      import.meta.env.VITE_STAGING === "true" ||
      import.meta.env.STAGING === "true";
    const defaultUrl = isStaging
      ? "https://staging.sieve.piwo.app"
      : "https://sieve.piwo.app";
    console.log(`No VITE_API_BASE_URL found, using default: ${defaultUrl}`);
    return defaultUrl;
  }

  // On the web the backend serves this bundle and the API on the same origin, so
  // a relative URL is always right: staging talks to staging, production to
  // production, and dev is proxied by vite. Guessing an absolute host here is
  // what made the staging site call the production backend, because the image
  // build sets no VITE_STAGING flag.
  return "";
};

/**
 * Headers for an API request.
 *
 * On web there is no Authorization header at all — the BFF session cookie is the
 * credential, and it rides along automatically. Native sends the bearer token it
 * obtained through its own PKCE flow.
 */
export const getApiHeaders = async (
  additionalHeaders: Record<string, string> = {},
): Promise<Record<string, string>> => {
  const accessToken = await getAccessToken();
  return {
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...additionalHeaders,
  };
};

/**
 * fetch() for the API: attaches credentials and turns an authentication failure
 * into a sign-in rather than an opaque error in the UI.
 */
/**
 * Like apiFetch, but a 401 is returned to the caller instead of starting a
 * sign-in. Used for probes such as "who am I?", where redirecting on failure
 * would fight with whatever the page is already doing.
 */
export const apiFetchAllowingUnauthorized = async (
  endpoint: string,
  init: RequestInit = {},
): Promise<Response> =>
  fetch(getApiUrl(endpoint), {
    ...init,
    credentials: "include",
    headers: await getApiHeaders(init.headers as Record<string, string> | undefined),
  });

export const apiFetch = async (endpoint: string, init: RequestInit = {}): Promise<Response> => {
  let response = await apiFetchAllowingUnauthorized(endpoint, init);

  if (response.status === 401) {
    // A rejection is not proof that the sign-in is over. The app refreshes on
    // its own reading of the expiry; the API reads the provider's. Renew once
    // against the token the API actually refused, and only give up if that
    // fails — every request bodied here carries a string, so replaying it is
    // safe.
    const renewed = await refreshAfterRejection();
    if (renewed) {
      response = await apiFetchAllowingUnauthorized(endpoint, init);
    }
  }

  if (response.status === 401) {
    // The session or token is gone. Start a fresh sign-in and stop the caller
    // from rendering a spurious error on the way out.
    const outcome = await beginLogin();

    if (outcome === "in-progress") {
      // An earlier attempt still owns the flow, so nothing was opened here. If
      // the user dismissed that one, every request from now on would fail with
      // a message promising a sign-in that never comes — so ask the app to put
      // a retry in front of them instead.
      announceAuthRequired();
      throw new Error("Sign-in was not completed — use the sign-in button to try again.");
    }

    throw new Error("Your session expired — signing you in again.");
  }

  return response;
};

/** Asks the UI for a visible sign-in prompt; harmless where there is no DOM. */
const announceAuthRequired = (): void => {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
};

/**
 * Get the full URL for an API endpoint
 * @param endpoint The API endpoint path (e.g., '/api/links')
 * @returns The full URL for the API endpoint
 */
export const getApiUrl = (endpoint: string): string => {
  const baseUrl = getApiBaseUrl();
  // Make sure the endpoint starts with a slash
  const normalizedEndpoint = endpoint.startsWith("/")
    ? endpoint
    : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
};

export type SyncAdapter = "outline";

export type DestinationItem = {
  adapter: SyncAdapter;
  id: string;
  title: string;
  subtitle?: string;
};

export type OutlineCollection = {
  id: string;
  name: string;
};

export type SyncSettings = {
  primaryAdapter: SyncAdapter;
  anyAdapterConnected: boolean;
  outline: {
    /** Whether this account has an Outline connection stored at all. */
    connected: boolean;
    /** The token is never returned by the API — only whether one exists. */
    baseUrl?: string;
    ready: boolean;
    reason?: string;
    collectionId?: string;
    collectionName?: string;
  };
};

const fetchJson = async <T>(endpoint: string, init?: RequestInit): Promise<T> => {
  const response = await apiFetch(endpoint, init);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      (errorData as { message?: string }).message ||
      `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return (await response.json()) as T;
};

export const listDestinations = async (
  adapter: SyncAdapter,
  query?: string,
): Promise<DestinationItem[]> => {
  const params = new URLSearchParams({ adapter });
  if (query?.trim()) {
    params.set("query", query.trim());
  }

  const data = await fetchJson<{ destinations?: DestinationItem[] }>(
    `/api/sync/destinations?${params.toString()}`,
  );
  return data.destinations || [];
};

export const createDestination = async (
  adapter: SyncAdapter,
  title: string,
): Promise<DestinationItem> => {
  const data = await fetchJson<{ destination: DestinationItem }>(
    "/api/sync/destinations",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ adapter, title }),
    },
  );
  return data.destination;
};

export const setSyncPreference = async (
  linkId: string,
  targetPageId?: string,
  targetAdapter?: SyncAdapter,
): Promise<void> => {
  await fetchJson(`/api/links/${linkId}/sync-preference`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ targetPageId, targetAdapter }),
  });
};

export const getSyncSettings = async (): Promise<SyncSettings> => {
  return fetchJson<SyncSettings>("/api/sync/settings");
};

export const listOutlineCollections = async (): Promise<OutlineCollection[]> => {
  const data = await fetchJson<{ collections?: OutlineCollection[] }>(
    "/api/sync/outline/collections",
  );
  return data.collections || [];
};

export const setPrimaryAdapter = async (primaryAdapter: SyncAdapter): Promise<void> => {
  await fetchJson("/api/sync/adapter", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ primaryAdapter }),
  });
};

export const setOutlineCollection = async (collectionId: string): Promise<void> => {
  await fetchJson("/api/sync/outline/collection", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ collectionId }),
  });
};

export const setOutlineCredentials = async (
  baseUrl: string,
  apiToken: string,
): Promise<{ ready: boolean; reason?: string }> =>
  fetchJson<{ ready: boolean; reason?: string }>("/api/sync/outline/credentials", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ baseUrl, apiToken }),
  });

export const clearOutlineCredentials = async (): Promise<void> => {
  await fetchJson("/api/sync/outline/credentials", { method: "DELETE" });
};

/** Everything accumulated from the processed links. See `/api/knowledge`. */

export type KnowledgeKind =
  | "place"
  | "song"
  | "artist"
  | "topic"
  | "tag"
  | "recipe"
  | "ingredient"
  | "domain"
  | "link";

export type KnowledgeEntity = {
  id: string;
  kind: KnowledgeKind;
  canonical_key: string;
  label: string;
  props: Record<string, unknown>;
  first_seen_at: string;
  last_seen_at: string;
  /** How many saved links mention it. */
  mentions: number;
};

export type KnowledgeNeighbour = KnowledgeEntity & {
  edge_kind: string;
  direction: "in" | "out";
  edge_props: Record<string, unknown>;
};

export type KnowledgeRelatedEntity = KnowledgeEntity & { depth: number };

export type KnowledgeLinkRef = {
  id: string;
  url: string;
  title: string | null;
  domain: string;
  status: string;
  created_at: string;
  depth: number;
};

export type KnowledgeStats = {
  nodes_by_kind: Array<{ kind: KnowledgeKind; count: number }>;
  edges_by_kind: Array<{ kind: string; count: number }>;
  total_nodes: number;
  total_edges: number;
  linked_links: number;
};

export const getKnowledgeStats = async (): Promise<KnowledgeStats> =>
  fetchJson<KnowledgeStats>("/api/knowledge/stats");

export const listKnowledgeEntities = async (options: {
  query?: string;
  kinds?: KnowledgeKind[];
  limit?: number;
  offset?: number;
} = {}): Promise<KnowledgeEntity[]> => {
  const params = new URLSearchParams();
  if (options.query?.trim()) params.set("q", options.query.trim());
  if (options.kinds?.length) params.set("kind", options.kinds.join(","));
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.offset !== undefined) params.set("offset", String(options.offset));

  const query = params.toString();
  return fetchJson<KnowledgeEntity[]>(`/api/knowledge/entities${query ? `?${query}` : ""}`);
};

/** The id may also be a name; the API resolves it to the best match. */
export const getKnowledgeEntity = async (
  id: string,
): Promise<KnowledgeEntity & { neighbours: KnowledgeNeighbour[] }> =>
  fetchJson(`/api/knowledge/entities/${encodeURIComponent(id)}`);

export const getRelatedKnowledgeEntities = async (
  id: string,
  options: { kinds?: KnowledgeKind[]; depth?: number; limit?: number } = {},
): Promise<KnowledgeRelatedEntity[]> => {
  const params = new URLSearchParams();
  if (options.kinds?.length) params.set("kind", options.kinds.join(","));
  if (options.depth !== undefined) params.set("depth", String(options.depth));
  if (options.limit !== undefined) params.set("limit", String(options.limit));

  const query = params.toString();
  return fetchJson<KnowledgeRelatedEntity[]>(
    `/api/knowledge/entities/${encodeURIComponent(id)}/related${query ? `?${query}` : ""}`,
  );
};

export const getKnowledgeEntityLinks = async (
  id: string,
  limit?: number,
): Promise<KnowledgeLinkRef[]> =>
  fetchJson<KnowledgeLinkRef[]>(
    `/api/knowledge/entities/${encodeURIComponent(id)}/links${limit ? `?limit=${limit}` : ""}`,
  );

export const rebuildKnowledge = async (): Promise<{ links: number }> =>
  fetchJson<{ links: number }>("/api/knowledge/rebuild", { method: "POST" });

/** Personal access tokens, for a user's own tools. See `/api/auth/tokens`. */

export type ApiToken = {
  id: string;
  name: string;
  /** Enough to recognise the token in this list; never enough to use it. */
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
};

/** Only ever returned once, by the request that created it. */
export type IssuedApiToken = ApiToken & { token: string };

export const listApiTokens = async (): Promise<ApiToken[]> =>
  fetchJson<ApiToken[]>("/api/auth/tokens");

export const createApiToken = async (
  name: string,
  expiresInDays?: number,
): Promise<IssuedApiToken> =>
  fetchJson<IssuedApiToken>("/api/auth/tokens", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, ...(expiresInDays ? { expiresInDays } : {}) }),
  });

export const revokeApiToken = async (id: string): Promise<void> => {
  await fetchJson(`/api/auth/tokens/${id}`, { method: "DELETE" });
};

/**
 * Where an MCP client should point. The API base is empty on the web, where the
 * backend serves this bundle from the same origin — a client outside the browser
 * needs the absolute URL, so fall back to the page's own origin.
 */
export const getMcpEndpointUrl = (): string => {
  const base = getApiBaseUrl() || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/api/mcp`;
};

/** The command that connects Claude Code to this installation, token included. */
export const buildMcpConnectCommand = (endpoint: string, token: string): string =>
  [
    "claude mcp add --transport http sieve \\",
    `  ${endpoint} \\`,
    `  --header "Authorization: Bearer ${token}"`,
  ].join("\n");
